import { useQuery } from '@tanstack/react-query'
import { cargasSalidaApi, muellesApi } from '@/services/api'
import type { CargaSalida, CuadroMuelles } from '@/types'

/** La raíz de las claves del cuadro. Se invalida **por prefijo**: mover un
 *  camión al martes siguiente cambia una semana que no es la visible, y una
 *  invalidación exacta dejaría esa otra semana mintiendo en la caché. */
export const CLAVE_CUADRO = 'dock-cuadro'
export const CLAVE_SIN_MUELLE = 'dock-sin-muelle'

export function claveCuadro(almacen: string | undefined, desde: string, hasta: string) {
  return [CLAVE_CUADRO, almacen ?? '*', desde, hasta] as const
}

/**
 * El cuadro de una ventana de tiempo.
 *
 * `staleTime` corto y **sin caché de servidor**: es una rejilla que dos personas
 * tocan a la vez, y servir dato viejo aquí es reservar dos veces el mismo muelle.
 */
export function useCuadroMuelles(params: {
  desde: string
  hasta: string
  almacen?: string
  enabled?: boolean
}) {
  const { desde, hasta, almacen, enabled = true } = params
  const q = useQuery({
    queryKey: claveCuadro(almacen, desde, hasta),
    queryFn: () => muellesApi.getCuadro({ desde, hasta, almacen }),
    enabled: enabled && Boolean(desde && hasta),
    staleTime: 30_000,
  })
  const cuadro: CuadroMuelles | undefined = q.data?.data
  return { ...q, cuadro }
}

/**
 * Las cargas que **no tienen camión**: el panel lateral del cuadro.
 *
 * Sale de la misma lista de la pantalla de cargas de salida, filtrando por
 * `camion == null`. No hay endpoint propio a propósito: dos consultas distintas
 * para la misma pregunta acabarían dando dos respuestas distintas.
 */
export function useCargasSinMuelle(almacen?: string) {
  const q = useQuery({
    queryKey: [CLAVE_SIN_MUELLE, almacen ?? '*'],
    queryFn: () => cargasSalidaApi.list({ almacen }),
    staleTime: 30_000,
  })
  const cargas: CargaSalida[] = (q.data?.data ?? []).filter((c) => !c.camion)
  return { ...q, cargas }
}
